import PropTypes from 'prop-types';
import React from 'react';
import Checkmark from '../../Icons/Checkmark';

import './RadioGroup.scss';

export class RadioGroup extends React.Component {
    static propTypes = {
        /**
         * css class that could override default classes
         */
        cssClass: PropTypes.string,
        /**
         * radio button group's identifier
         */
        name: PropTypes.string.isRequired,
        /**
         * currently selected value
         */
        selectedValue: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
        /**
         * function that will trigger on clicking radio button
         */
        onChange: PropTypes.func,
        /**
         * component that will display as checked symbol "✓" when the radio button is selected
         */
        checkMark: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.node,
        ]),
        /**
         * components that passed inside RadioGroup, currently support component 'Radio'
         */
        children: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.arrayOf(PropTypes.node),
        ]).isRequired,
    };

    static defaultProps = {
        cssClass: '',
        children: <span />,
        name: 'radio-button-group',
        selectedValue: '',
        onChange: () => {},
        checkMark: Checkmark,
    };

    /**
     * suppress React's warning on recognizing props as custom DOM attribute when using React.createElement
     */
    filterProps = (props) => {
        return props;
    };

    render() {
        const renderProps = this.filterProps(this.props);
        const {
            cssClass,
            children,
            name,
            selectedValue,
            onChange,
            checkMark,
            ...props
        } = renderProps;
        const childrenArray = React.Children.toArray(children);

        return (
            <div
                role="radiogroup"
                className={`component-Input-RadioGroup ${cssClass}`}
            >
                {childrenArray.map((child, index) =>
                    React.cloneElement(child, {
                        key: `${name}-${index}`,
                        name,
                        selectedValue,
                        onChange,
                        checkMark,
                        ...props,
                    })
                )}
            </div>
        );
    }
}

export class Radio extends React.Component {
    static propTypes = {
        /**
         * css class that could override default classes
         */
        cssClass: PropTypes.string,
        /**
         * passed from parent component 'RadioGroup', radio button group's identifier
         */
        name: PropTypes.string.isRequired,
        /**
         * passed from parent component 'RadioGroup', currently selected value
         */
        selectedValue: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
        /**
         * passed from parent component 'RadioGroup', function that will trigger on clicking radio button
         */
        onChange: PropTypes.func,
        /**
         * passed from parent component 'RadioGroup', component that will display as checked symbol "✓" when the radio button is selected
         */
        checkMark: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.node,
        ]),
        /**
         * value of the radio button
         */
        value: PropTypes.string.isRequired,
        /**
         * text that will display as label for the radio button
         */
        labelText: PropTypes.string,
    };

    static defaultProps = {
        name: 'radio-button-group',
        cssClass: '',
        selectedValue: '',
        onChange: () => {},
        value: '',
        labelText: '',
        checkMark: Checkmark,
    };

    handleChange = () => {
        const { onChange, value } = this.props;
        return typeof onChange === 'function' ? onChange(value) : () => {};
    };

    render() {
        const {
            cssClass,
            name,
            selectedValue,
            value,
            labelText,
            checkMark,
        } = this.props;
        const checked = value === selectedValue;
        const buttonCheckedStyle = checked ? 'display-btn-checked' : '';

        return (
            <label
                className={`radio-btn-wrapper ${cssClass}`}
                data-unit-test={value}
            >
                <input
                    className={`hidden-radio-btn`}
                    role="radio"
                    type="radio"
                    name={name}
                    onChange={this.handleChange}
                    checked={checked}
                    aria-checked={checked}
                />
                <span className={`display-btn ${buttonCheckedStyle}`}>
                    {checked && checkMark}
                </span>
                <span className={`label-text`}>{labelText}</span>
            </label>
        );
    }
}
